import React from 'react';
import { useTranslation } from 'next-i18next';
import {
  Typography,
  Box,
  Button,
  MenuItem,
  Alert,
  TextField,
  InputAdornment,
  ButtonBase,
} from '@mui/material';
// import DatePicker from '@mui/lab/DatePicker';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import { RiCalendar2Line } from 'react-icons/ri';

import Dinero from 'views/rebranded-module/common/utils/Dinero';
import moneyFormat from 'views/rebranded-module/common/utils/moneyFormat';
import { TOUR_TYPES } from 'lib/utils/CONSTANTS';
import Typography500 from 'views/common/components/UI/Typography500/Typography500';
import { FormErrors, Typography700 } from 'views/common/components';
import StyledBox, {
  StyledMenuPaper,
  StyledDatePicker,
} from 'views/rebranded-module/tours-module/common/components/TourBookingWidget/TourBookingWidget.styles';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import { TourPackageRadio } from 'views/rebranded-module/tours-module/common/components/TourBookingWidget/components/TourPackageRadio';
import { CounterV2 } from 'views/rebranded-module/common/components/UI/CounterV2';

const TourBookingWidget = (props) => {
  const {
    bookingDetails,
    includeBike,
    onIncludeBikeChange,
    handleBookTour,
    bookingError,
    startingPrice,
    disableDates,
    getBookingDetails,
    acceptableReservationsType,
    tourType,
    handleWidgetDataChange,
    widgetData,
    content,
  } = props;

  const { t } = useTranslation('fe_er_tour_package_selector');

  const handleDateChange = (date) => {
    const formatedDate = dayjs(date).format('YYYY-MM-DD');
    handleWidgetDataChange({ pickup_date: formatedDate });
    getBookingDetails({
      ...widgetData,
      pickup_date: formatedDate,
      with_motorcycle: includeBike,
    });
  };

  const handlePackageChange = (id) => {
    handleWidgetDataChange({ package_id: id });
  };

  const handleOccurenceChange = (e) => {
    const occurence = bookingDetails.occurrences.find(
      (occ) => occ.id === e.target.value,
    );

    handleWidgetDataChange({
      occurrence_id: occurence.id,
      pickup_date: occurence.start_date,
    });

    getBookingDetails({
      ...widgetData,
      occurrence_id: occurence.id,
      pickup_date: occurence.start_date,
      with_motorcycle: includeBike,
    });
  };

  const handleDecrement = () => {
    handleWidgetDataChange({ packages_count: widgetData.packages_count - 1 });
  };

  const handleIncrement = () => {
    handleWidgetDataChange({ packages_count: widgetData.packages_count + 1 });
  };

  const handleSelectTimeSlots = (item) => {
    handleWidgetDataChange({ availability_id: item.id });
  };

  const isMultiDayOffroad = bookingDetails?.tour?.is_multi_day_offroad;

  const renderOccurrenceName = (occurrence, selected) => {
    const date = dayjs(occurrence.start_date).format('MMM D, YYYY');
    const lang = occurrence.language ? `(${occurrence.language.name})` : '';
    const notes = occurrence.notes ? `(${occurrence.notes})` : '';

    const titleTextParts = [date, lang, notes];

    let badgeText = '';
    let badgeStyle = { display: 'inline-block' };
    let badgeClass = 'TBWOccurrenceBadge';
    if (occurrence.soldout) {
      badgeText = `(${t('fe_er_tour_package_selector:sold_out')})`;
      badgeClass += ' TBWSoldOutBadge';
    } else if (
      occurrence.maximum_vehicles_allowed
      && occurrence.maximum_vehicles_allowed <= 5
    ) {
      badgeText = `${[
        t('fe_er_tour_package_selector:only'),
        occurrence.maximum_vehicles_allowed,
        isMultiDayOffroad && t('fe_er_tour_package_selector:spots'),
        t('fe_er_tour_package_selector:left'),
      ]
        .filter(Boolean)
        .join(' ')}`;

      badgeText = selected ? badgeText : `(${badgeText})`;
      badgeStyle = { display: 'block' };
    } else if (occurrence.discount_percent) {
      const discount = `- ${occurrence.discount_percent}% ${t(
        'fe_er_tour_package_selector:occurrence_off',
      )}`;
      titleTextParts.splice(1, 0, discount);
    }

    return (
      <>
        <Box component="span">
          {titleTextParts.filter(Boolean).join(' ')}
          &nbsp;
        </Box>
        {badgeText && (
          <Box component="span" className={badgeClass} style={badgeStyle}>
            {badgeText}
          </Box>
        )}
      </>
    );
  };

  const renderSelectedOccurrence = (value) => {
    const occurrence = bookingDetails?.occurrences?.find((o) => o.id === value);
    if (!occurrence) return null;
    return renderOccurrenceName(occurrence, true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleIncludeBikeChange = (value) => {
    if (acceptableReservationsType === 'any_reservations') {
      onIncludeBikeChange(value);
      getBookingDetails({ with_motorcycle: value, ...widgetData });
      handleWidgetDataChange({ package_id: null });
    }
  };

  return (
    <StyledBox>
      {startingPrice && (
        <Typography500
          px={2}
          py={1}
          className="TBWStartingPrice"
          variant="body2"
          dangerouslySetInnerHTML={{
            __html: interpolateString(content.startingAtText, {
              price: moneyFormat(
                Dinero({
                  amount: startingPrice.amount,
                  currency: startingPrice.currency_code,
                  precision: startingPrice.precision,
                }),
              ),
            }),
          }}
        />
      )}

      <Box>
        {bookingDetails
        && (bookingDetails.tour.type_identifier === 'self_drive'
          || bookingDetails.tour.type_identifier === 'guided_recurring') ? (
            <>
              {bookingDetails?.calendar_options && (
              <StyledDatePicker
                required
                fullWidth
                className="TBWDatePicker"
                color="secondary"
                format="MMM D, YYYY"
                minDate={dayjs(bookingDetails.calendar_options.min_date)}
                maxDate={dayjs(bookingDetails.calendar_options.max_date)}
                id="tour_start_date"
                placeholder="mm/dd/yyyy"
                value={dayjs(
                  widgetData.pickup_date
                    || bookingDetails.calendar_options.min_date,
                )}
                onChange={(date) => {
                  handleDateChange(date);
                }}
                OpenPickerIcon={null}
                slotProps={{
                  popper: {
                    'data-testid': 'tour-start-date-popper',
                  },
                  previousIconButton: {
                    'data-testid': 'calendar-prev-month',
                  },
                  nextIconButton: {
                    'data-testid': 'calendar-next-month',
                  },
                  day: (ownerState) => ({
                    'data-testid': `calendar-day-${dayjs(
                      ownerState?.day,
                    ).date()}`,
                  }),
                  textField: {
                    className: 'TBWDatePickerTextField',
                    inputProps: {
                      'data-testid': 'tour_start_date',
                    },
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <RiCalendar2Line />
                        </InputAdornment>
                      ),
                      endAdornment: null,
                    },
                    fullWidth: true,
                  },
                }}
                shouldDisableDate={disableDates}
                disableCloseOnSelect={false}
              />
              )}
              {bookingError?.errors && (
              <FormErrors errorMsgs={bookingError} errorField="pickup_date" />
              )}
            </>
          ) : (
            <>
              {bookingDetails?.occurrences?.length > 0 && (
              <TextField
                data-testid="tour-occurrence-select"
                value={
                  widgetData.occurrence_id || bookingDetails.occurrences[0].id
                }
                id="tour_date"
                name="tour_date"
                variant="outlined"
                className="TBWPackageSelector"
                onChange={(e) => handleOccurenceChange(e)}
                fullWidth
                select
                SelectProps={{
                  renderValue: renderSelectedOccurrence,
                  MenuProps: {
                    PaperProps: {
                      component: StyledMenuPaper,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RiCalendar2Line />
                    </InputAdornment>
                  ),
                }}>
                {bookingDetails.occurrences.map((occurrence) => (
                  <MenuItem
                    value={occurrence.id}
                    key={occurrence.id}
                    className="TBWPackageItem">
                    {renderOccurrenceName(occurrence)}
                  </MenuItem>
                ))}
              </TextField>
              )}

              {bookingError?.errors?.occurrence_id?.length > 0 && (
              <Alert
                className="TBWError"
                severity="error"
                icon={<ErrorIcon className="TBWErrorIcon" />}>
                {bookingError.errors.occurrence_id.join(', ')}
              </Alert>
              )}
            </>
          )}
      </Box>
      {bookingDetails?.packages && (
        <Box mt={2}>
          {bookingDetails?.packages.map((pack, index) => (
            <Box my={1}>
              <TourPackageRadio
                content={content}
                offRoadPackages={
                  bookingDetails.tour.type_identifier === 'guided_recurring'
                }
                packageName={
                  bookingDetails.tour.type_identifier === 'guided_recurring'
                    ? interpolateString(content.offroadPackageText, {
                      seater_number: pack.people_count,
                    })
                    : null
                }
                index={index}
                id={pack.id}
                key={pack.id}
                adultsNumber={pack.people_count}
                numberOfMoto={pack.motorcycles_count}
                numberOfBed={pack.beds_count}
                pricePerPerson={moneyFormat(
                  Dinero({
                    amount: pack.price_per_person.amount,
                    currency: pack.price_per_person.currency_code,
                  }),
                )}
                active={pack.id == widgetData.package_id}
                onClick={handlePackageChange}
                showPriceOnly={
                  tourType.identifier === TOUR_TYPES.GUIDED_OFFROAD
                }
                isMultiDayOffroad={pack.is_multi_day_offroad}
                minPersons={pack.min_persons}
                maxPersons={pack.max_persons}
              />
            </Box>
          ))}
          {bookingError?.errors?.package_id?.length > 0 && (
            <Alert
              className="TBWError"
              severity="error"
              icon={<ErrorIcon className="TBWErrorIcon" />}>
              {bookingError.errors.package_id.join(', ')}
            </Alert>
          )}
        </Box>
      )}

      {bookingDetails?.recurring_tour_availabilities.length > 0 && (
        <Box mt={2} id="time_slots" px={2}>
          <Typography500 variant="body1" className="TBWTimeText" py={1}>
            {content.timeSlotsText}
          </Typography500>
          <Box className="TBWSlotsWrapper">
            {bookingDetails.recurring_tour_availabilities.map((item) => (
              <ButtonBase
                key={item}
                className={`TBWTimeSlot ${
                  item.id === widgetData.availability_id ? 'TBWActive' : ''
                }`}
                data-testid="tour-time-slot-option"
                onClick={() => handleSelectTimeSlots(item)}>
                <Typography700 className="TBWTimeSlotText" variant="body2">
                  {item.time_part}
                </Typography700>
              </ButtonBase>
            ))}
          </Box>
          {bookingError?.errors?.availability?.length > 0 && (
            <Alert
              className="TBWError"
              severity="error"
              icon={<ErrorIcon className="TBWErrorIcon" />}>
              {bookingError.errors.availability.join(', ')}
            </Alert>
          )}
        </Box>
      )}

      <Box mt={3.5} px={1.5}>
        <CounterV2
          count={widgetData.packages_count || 1}
          min={1}
          max={10}
          onIncrement={handleIncrement}
          onDecrement={handleDecrement}
          inactive={false}
          incrementTestId="tour-packages-increment"
          decrementTestId="tour-packages-decrement"
          title={
            bookingDetails?.tour.type_identifier === 'guided_recurring'
              ? content.offroadNoOfPackagesText
              : content.numberOfPackagesText
          }
        />

        {bookingError?.errors?.packages_count?.length > 0 && (
          <Alert
            className="TBWError"
            severity="error"
            icon={<ErrorIcon className="TBWErrorIcon" />}>
            {bookingError.errors.packages_count.join(', ')}
          </Alert>
        )}
      </Box>
      <Box mt={2.25}>
        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={handleBookTour}
          data-testid="book-tour-btn"
          color="primary">
          {content.bookBtnText}
        </Button>
      </Box>

      <Typography
        pt={2.5}
        pb={2}
        variant="body2"
        display="block"
        align="center">
        {content.widgetNoteText}
      </Typography>
    </StyledBox>
  );
};

export { TourBookingWidget };
